/*
 * "Continua a ver": on the product page, shows the products that follow the current one in the
 * collection the shopper came from. The collection comes from collection-memory.js, and is only
 * used when the product really belongs to it. The items are fetched with the section rendering API
 * once the strip is close to the viewport, and only the few shown are added to the page, so no
 * extra images are downloaded.
 */
if (!customElements.get('product-continue')) {
  customElements.define(
    'product-continue',
    class ProductContinue extends HTMLElement {
      connectedCallback() {
        this.handle = this.rememberedHandle();
        if (!this.handle) return;

        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        // The content is hidden until it has products, so watch a 1px marker placed just before it.
        const anchor = this.closest('.shopify-section')?.querySelector('[data-continue-sentinel]') || this.parentElement;
        this.observer = new IntersectionObserver(
          (entries) => {
            // Also when the marker is already above the viewport (a fast scroll or a restored position).
            if (!entries.some((entry) => entry.isIntersecting || entry.boundingClientRect.top < 0)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '800px 0px' }
        );
        this.observer.observe(anchor);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      rememberedHandle() {
        let handle = null;
        try {
          handle = sessionStorage.getItem('eva:from-collection');
        } catch (error) {
          return null;
        }
        const own = (this.dataset.collections || '').split(',').filter(Boolean);
        return handle && own.includes(handle) ? handle : null;
      }

      async load() {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}collections/${this.handle}?sections=collection-continue-items`);
          if (!response.ok) return;

          const data = await response.json();
          const doc = new DOMParser().parseFromString(data['collection-continue-items'] || '', 'text/html');
          const source = doc.querySelector('[data-continue-source]');
          const items = [...doc.querySelectorAll('li[data-product-id]')];
          if (!source || items.length < 2) return;

          // The products after the current one, wrapping around to the start of the collection.
          const current = items.findIndex((item) => item.dataset.productId === this.dataset.productId);
          const ordered = current >= 0 ? items.slice(current + 1).concat(items.slice(0, current)) : items;
          const picked = ordered.slice(0, Number(this.dataset.limit) || 8);
          if (!picked.length) return;

          const grid = this.querySelector('[data-continue-grid]');
          picked.forEach((item) => grid.append(document.importNode(item, true)));

          this.querySelector('[data-continue-heading]').textContent = (this.dataset.heading || '').replace(
            '{collection}',
            source.dataset.title || ''
          );
          const link = this.querySelector('[data-continue-link]');
          if (link && source.dataset.url) link.href = source.dataset.url;

          this.closest('[data-continue-wrapper]').hidden = false;
          document.documentElement.classList.add('continue-collection-visible');
        } catch (error) {
          console.error(error);
        }
      }
    }
  );
}
